import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomerService from '../../services/CustomerService';
import UserService from '../../services/UserService';
import { useAuthUser } from '../auth/useAuthUser';
import RestaurantDiningRoomService from '../../services/restaurant/RestaurantDiningRoomService';
import RestaurantMenuCategoryService from '../../services/restaurant/RestaurantMenuCategoryService';
import RestaurantMenuItemService from '../../services/restaurant/RestaurantMenuItemService';
import RestaurantKitchenOrderService from '../../services/restaurant/RestaurantKitchenOrderService';
import RestaurantOrderService, {
  RestaurantSplitCheckInput,
} from '../../services/restaurant/RestaurantOrderService';
import RestaurantTableService from '../../services/restaurant/RestaurantTableService';
import RestaurantCatalogExtensionService from '../../services/restaurant/RestaurantCatalogExtensionService';
import RestaurantSettingsService from '../../features/restaurant/settings/RestaurantSettingsService';
import { RestaurantPosSettings } from '../../features/restaurant/settings/RestaurantSettingsTypes';
import { RestaurantComboDTO } from '../../types/restaurant/RestaurantCatalogExtensionTypes';
import { AddRestaurantOrderItemRequest } from '../../types/restaurant/RestaurantOrderTypes';
import {
  RestaurantKitchenPrintJobDTO,
  RestaurantKitchenStationPrintState,
} from '../../types/restaurant/RestaurantKitchenOrderTypes';
import { RestaurantDiningRoomDTO } from '../../types/restaurant/RestaurantDiningRoomTypes';
import {
  RestaurantMenuCategoryDTO,
  RestaurantMenuItemDTO,
} from '../../types/restaurant/RestaurantMenuTypes';
import {
  EMPTY_MENU_FILTERS,
  RestaurantOrderDTO,
  RestaurantOrderMenuFilters,
  RestaurantOrderServiceType,
  RestaurantPartyOption,
} from '../../types/restaurant/RestaurantOrderTypes';
import { RestaurantTableDTO } from '../../types/restaurant/RestaurantTableTypes';
import { setKitchenOrders } from '../../store/slices/restaurant/restaurantKitchenOrderSlice';
import { AppDispatch, RootState } from '../../store/store';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';
import useRestaurantOrderBilling from './useRestaurantOrderBilling';
import useContinueSale from '../sale/useContinueSale';
import { processKitchenPrintJobs } from '../../services/printing/kitchen-print.service';

const DEFAULT_POS_FLAGS = {
  splitBillEnabled: true,
  allowTableTransfer: true,
  allowMergeOrders: true,
  allowMoveOrderItems: true,
  allowReopenOrder: true,
  kitchenWorkflowEnabled: true,
};

const arrayFrom = (value: any): any[] => {
  const data = value?.data ?? value;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const partiesFrom = (value: any): RestaurantPartyOption[] => {
  return arrayFrom(value)
    .map((item) => ({
      id: Number(item.id),
      code: item.code ?? null,
      defaultCustomer: item.defaultCustomer === true,
      name:
        item.name ||
        item.fullName ||
        item.tradeName ||
        item.username ||
        `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() ||
        `#${item.id}`,
    }))
    .filter((item) => item.id)
    .sort(
      (left, right) =>
        Number(right.defaultCustomer) - Number(left.defaultCustomer)
    );
};

const useRestaurantOrders = (branchId: number | '') => {
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirmModal = useConfirm();
  const continueSale = useContinueSale();
  const dispatch = useDispatch<AppDispatch>();
  const { hasRole } = useAuthUser();
  // Solo SUPPORT puede elegir manualmente el mesero de una orden (ver
  // RestaurantOrders.tsx); para cualquier otro rol ni pedimos el listado de
  // usuarios (evita un 403 innecesario en la consola).
  const canChooseWaiter = Boolean(hasRole('SUPPORT'));
  const kitchenFilters = useSelector(
    (state: RootState) => state.restaurantKitchenOrder.filters
  );

  const [rooms, setRooms] = useState<RestaurantDiningRoomDTO[]>([]);
  const [tables, setTables] = useState<RestaurantTableDTO[]>([]);
  const [categories, setCategories] = useState<RestaurantMenuCategoryDTO[]>([]);
  const [menuItems, setMenuItems] = useState<RestaurantMenuItemDTO[]>([]);
  const [combos, setCombos] = useState<RestaurantComboDTO[]>([]);
  const [customers, setCustomers] = useState<RestaurantPartyOption[]>([]);
  const [waiters, setWaiters] = useState<RestaurantPartyOption[]>([]);

  const [selectedRoomId, setSelectedRoomId] = useState<number | 'ALL'>('ALL');
  const selectedRoomIdRef = useRef<number | 'ALL'>('ALL');
  useEffect(() => {
    selectedRoomIdRef.current = selectedRoomId;
  }, [selectedRoomId]);
  const roomParam = () =>
    selectedRoomIdRef.current === 'ALL'
      ? undefined
      : (selectedRoomIdRef.current as number);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | 'ALL'>(
    'ALL'
  );
  const [menuSearch, setMenuSearch] = useState('');
  const [debouncedMenuSearch, setDebouncedMenuSearch] = useState('');
  const [menuFilters, setMenuFilters] =
    useState<RestaurantOrderMenuFilters>(EMPTY_MENU_FILTERS);
  const [menuLoading, setMenuLoading] = useState(false);
  const [selectedTable, setSelectedTable] = useState<RestaurantTableDTO | null>(
    null
  );
  const [order, setOrder] = useState<RestaurantOrderDTO | null>(null);
  const [posFlags, setPosFlags] = useState(DEFAULT_POS_FLAGS);
  const [serviceType, setServiceTypeState] =
    useState<RestaurantOrderServiceType>('DINE_IN');
  const [tablelessOrders, setTablelessOrders] = useState<RestaurantOrderDTO[]>(
    []
  );
  const [tablelessLoading, setTablelessLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [kitchenPrintStates, setKitchenPrintStates] = useState<
    RestaurantKitchenStationPrintState[]
  >([]);
  const mutationRef = useRef(false);

  const updateKitchenPrintState = (
    job: RestaurantKitchenPrintJobDTO,
    status: RestaurantKitchenStationPrintState['status'],
    error?: unknown
  ) => {
    setKitchenPrintStates((current) => {
      const next: RestaurantKitchenStationPrintState = {
        jobId: job.jobId,
        preparationStationId: job.preparationStationId,
        preparationStationName: job.preparationStationName,
        status,
        ...(error
          ? {
              error:
                (error as Error)?.message ||
                'No se pudo imprimir la comanda de esta estación.',
            }
          : {}),
      };
      const index = current.findIndex((item) => item.jobId === job.jobId);
      if (index < 0) return [...current, next];
      const states = [...current];
      states[index] = next;
      return states;
    });
  };

  const refreshTables = useCallback(async () => {
    if (!branchId) return;

    const response = await RestaurantTableService.list(
      branchId,
      true,
      roomParam()
    );
    setTables(response.data ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const ACTIVE_ORDER_STATUSES = [
    'OPEN',
    'CONFIRMED',
    'IN_KITCHEN',
    'READY',
    'BILLING',
  ];

  const loadTablelessOrders = useCallback(
    async (type: RestaurantOrderServiceType) => {
      if (!branchId || type === 'DINE_IN') return;

      setTablelessLoading(true);
      try {
        const response = await RestaurantOrderService.list(branchId, {
          serviceType: type,
        });
        setTablelessOrders(
          (response.data ?? []).filter((item) =>
            ACTIVE_ORDER_STATUSES.includes(item.status)
          )
        );
      } catch (error) {
        handle(error);
      } finally {
        setTablelessLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [branchId]
  );

  const setServiceType = (next: RestaurantOrderServiceType) => {
    setServiceTypeState(next);
    setSelectedTable(null);
    setOrder(null);

    if (next === 'DINE_IN') {
      setTablelessOrders([]);
    } else {
      loadTablelessOrders(next);
    }
  };

  const startNewTablelessOrder = () => {
    setSelectedTable(null);
    setOrder(null);
  };

  // Mantiene sincronizada la tarjeta de la lista "Para llevar"/"Domicilio"
  // (total, estado) con la orden actual, sin esperar a un refresh manual:
  // cada mutación exitosa (agregar producto, cambiar cantidad, cobrar, etc.)
  // actualiza `order`, y este efecto refleja ese mismo objeto en la lista.
  useEffect(() => {
    if (!order || order.tableId != null) return;

    setTablelessOrders((current) => {
      const stillActive = ACTIVE_ORDER_STATUSES.includes(order.status);
      const exists = current.some((item) => item.id === order.id);

      if (!stillActive) {
        return exists ? current.filter((item) => item.id !== order.id) : current;
      }
      if (!exists) return [order, ...current];
      return current.map((item) => (item.id === order.id ? order : item));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const refreshCustomers = useCallback(async () => {
    const response = await CustomerService.getDropdown();
    const nextCustomers = partiesFrom(response);
    setCustomers(nextCustomers);
    return nextCustomers;
  }, []);

  const refreshOperationalState = useCallback(async () => {
    if (!branchId) return;

    const [, tableResult, kitchenResult] = await Promise.allSettled([
      RestaurantOrderService.list(branchId),
      RestaurantTableService.list(branchId, true, roomParam()),
      RestaurantKitchenOrderService.list(branchId, kitchenFilters),
    ]);

    if (tableResult.status === 'fulfilled') {
      setTables(tableResult.value.data ?? []);
    }

    if (kitchenResult.status === 'fulfilled') {
      dispatch(setKitchenOrders(kitchenResult.value.data ?? []));
    }
  }, [branchId, dispatch, kitchenFilters]);

  const printKitchenJobs = async (kitchenOrderId: number) => {
    try {
      const jobs = await RestaurantKitchenOrderService.printJobs(
        kitchenOrderId
      );
      setKitchenPrintStates([]);
      const results = await processKitchenPrintJobs(
        jobs,
        undefined,
        updateKitchenPrintState
      );

      results
        .filter((result) => !result.success)
        .forEach((result) =>
          show(
            'error',
            `Error de impresión · ${result.job.preparationStationName}`,
            (result.error as Error)?.message ||
              'No se pudo imprimir la comanda de esta estación.'
          )
        );
    } catch (error) {
      handle(error);
    }
  };

  const load = async () => {
    if (!branchId) return;

    setLoading(true);
    setOrder(null);
    setSelectedTable(null);
    setServiceTypeState('DINE_IN');
    setTablelessOrders([]);
    setKitchenPrintStates([]);

    try {
      const [roomResponse, tableResponse, categoryResponse, comboResponse] =
        await Promise.all([
          RestaurantDiningRoomService.list(branchId, true),
          RestaurantTableService.list(branchId, true, roomParam()),
          RestaurantMenuCategoryService.list(branchId, true),
          RestaurantCatalogExtensionService.listCombos(branchId, {
            page: 0,
            size: 200,
            active: true,
            available: true,
          }),
        ]);

      setRooms(roomResponse.data ?? []);
      setTables(tableResponse.data ?? []);
      setCategories(categoryResponse.data ?? []);
      setCombos(comboResponse.content);
      // Los ítems de menú se cargan (y filtran) desde el backend en un efecto aparte.

      const [customerResult, waiterResult, settingsResult] =
        await Promise.allSettled([
          CustomerService.getDropdown(),
          canChooseWaiter
            ? UserService.findAllUsersWithPagination(0, 500)
            : Promise.resolve(null),
          RestaurantSettingsService.getByBranch(branchId),
        ]);

      setCustomers(
        customerResult.status === 'fulfilled'
          ? partiesFrom(customerResult.value)
          : []
      );

      setWaiters(
        waiterResult.status === 'fulfilled' && waiterResult.value
          ? partiesFrom(waiterResult.value)
          : []
      );

      if (settingsResult.status === 'fulfilled') {
        const pos: RestaurantPosSettings | undefined =
          settingsResult.value?.data?.pos;
        const kitchen = settingsResult.value?.data?.kitchen;
        if (pos) {
          setPosFlags({
            splitBillEnabled: pos.splitBillEnabled !== false,
            allowTableTransfer: pos.allowTableTransfer !== false,
            allowMergeOrders: pos.allowMergeOrders !== false,
            allowMoveOrderItems: pos.allowMoveOrderItems !== false,
            allowReopenOrder: pos.allowReopenOrder !== false,
            kitchenWorkflowEnabled: kitchen?.kitchenWorkflowEnabled !== false,
          });
        }
      } else {
        setPosFlags(DEFAULT_POS_FLAGS);
      }
    } catch (error) {
      handle(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (branchId) load();
  }, [branchId]);

  // Filtro de salón: recarga las mesas desde el backend al cambiar de salón.
  const isFirstRoomLoad = useRef(true);
  useEffect(() => {
    if (isFirstRoomLoad.current) {
      isFirstRoomLoad.current = false;
      return;
    }
    if (branchId) refreshTables();
  }, [branchId, selectedRoomId, refreshTables]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedMenuSearch(menuSearch), 300);
    return () => clearTimeout(handle);
  }, [menuSearch]);

  const {
    availability: menuAvailability,
    featuredOnly: menuFeaturedOnly,
    priceMin: menuPriceMin,
    priceMax: menuPriceMax,
  } = menuFilters;

  useEffect(() => {
    if (!branchId) {
      setMenuItems([]);
      return;
    }

    let cancelled = false;
    const available =
      menuAvailability === 'ALL'
        ? null
        : menuAvailability === 'UNAVAILABLE'
        ? false
        : undefined;
    const priceMin = menuPriceMin.trim() ? Number(menuPriceMin) : undefined;
    const priceMax = menuPriceMax.trim() ? Number(menuPriceMax) : undefined;

    setMenuLoading(true);
    RestaurantMenuItemService.listAvailable(branchId, {
      categoryId: selectedCategoryId === 'ALL' ? undefined : selectedCategoryId,
      search: debouncedMenuSearch.trim() || undefined,
      available,
      featured: menuFeaturedOnly || undefined,
      priceMin,
      priceMax,
    })
      .then((response) => {
        if (!cancelled) {
          setMenuItems((response.data ?? []).filter((item) => item.active));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          handle(error);
          setMenuItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setMenuLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    branchId,
    selectedCategoryId,
    debouncedMenuSearch,
    menuAvailability,
    menuFeaturedOnly,
    menuPriceMin,
    menuPriceMax,
  ]);

  const selectTable = async (table: RestaurantTableDTO) => {
    if (!['FREE', 'OCCUPIED'].includes(table.status)) return;

    setServiceTypeState('DINE_IN');
    setSelectedTable(table);

    if (table.status === 'FREE') {
      setOrder(null);
      return;
    }

    setBusy(true);

    try {
      const response = await RestaurantOrderService.activeByTable(table.id);
      setOrder(response.data);
    } catch (error) {
      handle(error);
      setOrder(null);
    } finally {
      setBusy(false);
    }
  };

  const openOrderById = async (orderId: number) => {
    if (!orderId) return;
    setBusy(true);
    try {
      const response = await RestaurantOrderService.get(orderId);
      setOrder(response.data);
      setServiceTypeState(response.data.serviceType ?? 'DINE_IN');
      const table = tables.find((item) => item.id === response.data.tableId);
      setSelectedTable(table ?? null);
    } catch (error) {
      handle(error);
    } finally {
      setBusy(false);
    }
  };

  const selectTablelessOrder = (orderId: number) => openOrderById(orderId);

  const run = async (
    operation: () => Promise<any>,
    fallbackTitle: string,
    successMessage?: string
  ) => {
    if (mutationRef.current) return;

    mutationRef.current = true;
    setBusy(true);

    try {
      const response = await operation();

      setOrder(response.data);

      show(
        response.type ?? 'success',
        response.title ?? fallbackTitle,
        successMessage ?? response.message ?? 'Operación completada.'
      );

      try {
        await refreshTables();
      } catch (error) {
        handle(error);
      }
      return response;
    } catch (error) {
      handle(error);
    } finally {
      mutationRef.current = false;
      setBusy(false);
    }
  };

  const create = (
    customerId: number | '',
    waiterId: number | '',
    notes: string
  ) => {
    if (!branchId) return;
    if (
      serviceType === 'DINE_IN' &&
      (!selectedTable || selectedTable.status !== 'FREE')
    ) {
      return;
    }

    return run(
      () =>
        RestaurantOrderService.create(branchId, {
          serviceType,
          ...(serviceType === 'DINE_IN' ? { tableId: selectedTable!.id } : {}),
          ...(customerId === '' ? {} : { customerId: Number(customerId) }),
          ...(waiterId === '' ? {} : { waiterId: Number(waiterId) }),
          reservationId: null,
          notes: notes.trim() || null,
        }),
      'Orden abierta'
    ).then((response) => {
      if (response && serviceType !== 'DINE_IN') loadTablelessOrders(serviceType);
      return response;
    });
  };

  const changeCustomer = (customerId: number, successMessage?: string) => {
    if (
      !order ||
      !Number.isFinite(customerId) ||
      customerId <= 0 ||
      order.customerId === customerId ||
      !['OPEN', 'CONFIRMED', 'IN_KITCHEN', 'READY'].includes(order.status)
    ) {
      return;
    }

    return run(
      () => RestaurantOrderService.changeCustomer(order.id, customerId),
      'Cliente actualizado',
      successMessage
    );
  };

  const addItem = (body: AddRestaurantOrderItemRequest) => {
    if (!order || order.status !== 'OPEN') return;
    const hasMenuItem = body.menuItemId !== null;
    const hasCombo = body.comboMenuId !== null;
    if (hasMenuItem === hasCombo) return;
    if (hasCombo && body.modifierIds.length > 0) return;

    return run(
      () => RestaurantOrderService.addItem(order.id, body),
      'Ítem agregado'
    );
  };

  const updateQuantity = (itemId: number, quantity: number) => {
    if (!order || order.status !== 'OPEN' || quantity < 0) return;
    const item = order.items.find((current) => current.id === itemId);
    if (!item?.editable) return;

    return run(
      () => RestaurantOrderService.updateQuantity(order.id, itemId, quantity),
      'Cantidad actualizada'
    );
  };

  const updateComboQuantity = (comboInstanceId: string, quantity: number) => {
    if (!order || order.status !== 'OPEN' || quantity < 0) return;

    return run(
      () =>
        RestaurantOrderService.updateComboQuantity(
          order.id,
          comboInstanceId,
          quantity
        ),
      'Cantidad del combo actualizada'
    );
  };

  const cancelItem = (itemId: number, label: string) => {
    if (!order || order.status !== 'OPEN') return;
    const item = order.items.find((current) => current.id === itemId);
    if (!item?.editable) return;

    confirmModal({
      title: 'Eliminar producto',
      message: `¿Desea eliminar “${label}”? Si pertenece a un combo, se eliminarán todos sus componentes.`,
      icon: 'warning',
      confirmText: 'Sí, eliminar',
      cancelText: 'Volver',
      onConfirm: () =>
        run(
          () => RestaurantOrderService.cancelItem(order.id, itemId),
          'Producto eliminado'
        ),
    });
  };

  const confirmOrder = () => {
    if (!order || order.status !== 'OPEN') return;

    const hasPendingItems = order.items.some(
      (item) => item.editable === true && item.sentToKitchen === false
    );
    const wasDispatched = order.items.some((item) => item.sentToKitchen);
    // Sin productos nuevos y nunca enviada => nada que hacer.
    if (!hasPendingItems && !wasDispatched) return;

    return run(
      () => RestaurantOrderService.confirm(order.id),
      hasPendingItems ? 'Comanda enviada' : 'Orden lista para cobrar'
    ).then(async (response) => {
      if (response) {
        await refreshOperationalState();
        if (
          response.data.kitchenPrintRequired === true &&
          response.data.kitchenOrderId
        ) {
          await printKitchenJobs(response.data.kitchenOrderId);
        }
      }
      return response;
    });
  };

  const reopenOrder = () => {
    if (!order || order.status !== 'READY') return;

    return run(
      () => RestaurantOrderService.reopen(order.id),
      'Orden reabierta'
    ).then(async (response) => {
      if (response) await refreshOperationalState();
      return response;
    });
  };

  const cancelOrder = () => {
    if (!order || order.status !== 'OPEN') return;
    // Ya enviada a cocina => no se puede cancelar, debe cobrarse.
    if (order.items.some((item) => item.sentToKitchen)) return;

    confirmModal({
      title: 'Cancelar orden',
      message: 'Esta acción cancelará la orden completa y liberará la mesa.',
      icon: 'warning',
      confirmText: 'Sí, cancelar orden',
      cancelText: 'Volver',
      onConfirm: async () => {
        const response = await run(
          () => RestaurantOrderService.cancel(order.id),
          'Orden cancelada'
        );

        if (response) {
          setOrder(null);
          setSelectedTable(null);
          await refreshOperationalState();
        }
      },
    });
  };

  const transferTable = (targetTableId: number) => {
    if (!order || !posFlags.allowTableTransfer) return;
    if (!['OPEN', 'CONFIRMED', 'IN_KITCHEN', 'READY'].includes(order.status)) {
      return;
    }

    return run(
      () => RestaurantOrderService.transferTable(order.id, targetTableId),
      'Orden trasladada'
    ).then(async (response) => {
      if (response) {
        try {
          const fresh = await RestaurantTableService.list(branchId as number, true);
          const nextTable = (fresh.data ?? []).find(
            (item) => item.id === targetTableId
          );
          setTables(fresh.data ?? []);
          if (nextTable) setSelectedTable(nextTable);
        } catch (error) {
          handle(error);
        }
        await refreshOperationalState();
      }
      return response;
    });
  };

  const loadTransferTargets = useCallback(
    async (filters?: { diningRoomId?: number; search?: string }) => {
      if (!order) return [];
      try {
        const response = await RestaurantOrderService.transferTargets(
          order.id,
          filters
        );
        return response.data ?? [];
      } catch (error) {
        handle(error);
        return [];
      }
    },
    [handle, order]
  );

  const loadOrderItems = useCallback(
    async (filters?: { search?: string; movableOnly?: boolean }) => {
      if (!order) return [];
      try {
        const response = await RestaurantOrderService.orderItems(
          order.id,
          filters
        );
        return response.data ?? [];
      } catch (error) {
        handle(error);
        return [];
      }
    },
    [handle, order]
  );

  const loadFreeTables = useCallback(
    async (filters?: { diningRoomId?: number; search?: string }) => {
      if (!branchId) return [];
      try {
        const response = await RestaurantTableService.listForTransfer(
          branchId,
          { ...filters, status: 'FREE' }
        );
        return response.data ?? [];
      } catch (error) {
        handle(error);
        return [];
      }
    },
    [branchId, handle]
  );

  const moveOrderItems = (targetOrderId: number, itemIds: number[]) => {
    if (!order || !posFlags.allowMoveOrderItems || !itemIds.length) return;
    if (mutationRef.current) return;

    mutationRef.current = true;
    setBusy(true);

    return RestaurantOrderService.moveItems(order.id, targetOrderId, itemIds)
      .then(async (response) => {
        show(
          response.type ?? 'success',
          response.title ?? 'Productos movidos',
          response.message ?? 'Los productos se movieron a la otra orden.'
        );
        try {
          const source = await RestaurantOrderService.get(order.id);
          setOrder(source.data);
        } catch (error) {
          handle(error);
        }
        await refreshOperationalState();
        return response;
      })
      .catch((error) => {
        handle(error);
        return undefined;
      })
      .finally(() => {
        mutationRef.current = false;
        setBusy(false);
      });
  };

  const mergeOrders = (targetOrderId: number, sourceOrderId?: number) => {
    if (!order || !posFlags.allowMergeOrders) return;
    const target = sourceOrderId == null ? order.id : targetOrderId;
    const source = sourceOrderId == null ? targetOrderId : sourceOrderId;
    if (target === source) return;

    return run(
      () => RestaurantOrderService.merge(target, source),
      'Órdenes fusionadas'
    ).then(async (response) => {
      if (response) await refreshOperationalState();
      return response;
    });
  };

  const splitBill = (checks: RestaurantSplitCheckInput[]) => {
    if (!order || !posFlags.splitBillEnabled) return;

    return run(
      () => RestaurantOrderService.splitChecks(order.id, checks),
      'Cuenta dividida'
    );
  };

  const clearSplitBill = () => {
    if (!order) return;

    return run(
      () => RestaurantOrderService.clearChecks(order.id),
      'División eliminada'
    );
  };

  const billCheck = async (checkId: number, documentTypeId: number) => {
    if (!order) return false;
    if (!order.customerId) {
      show(
        'warning',
        'Cliente requerido',
        'Asigne un cliente a la orden antes de iniciar el cobro.'
      );
      return false;
    }

    try {
      const response = await RestaurantOrderService.billCheck(
        order.id,
        checkId,
        documentTypeId
      );
      show(
        response.type ?? 'success',
        response.title ?? 'Cobro iniciado',
        response.message ?? 'La subcuenta fue enviada a caja.'
      );
      continueSale(response.data);
      return true;
    } catch (error) {
      handle(error);
      return false;
    }
  };

  const clearClosedOrder = useCallback(async () => {
    setOrder(null);
    setSelectedTable(null);
    await refreshOperationalState();
  }, [refreshOperationalState]);

  const orderBilling = useRestaurantOrderBilling({
    order,
    setOrder,
    refreshTables,
    onOrderClosed: clearClosedOrder,
  });

  const visibleCombos = useMemo(() => {
    const query = debouncedMenuSearch.trim().toLowerCase();
    if (!query) return combos;
    return combos.filter((combo) =>
      `${combo.code} ${combo.name}`.toLowerCase().includes(query)
    );
  }, [combos, debouncedMenuSearch]);

  return {
    rooms,
    tables,
    categories,
    menuItems,
    combos: visibleCombos,
    customers,
    waiters,

    selectedRoomId,
    setSelectedRoomId,
    selectedCategoryId,
    setSelectedCategoryId,
    menuSearch,
    setMenuSearch,
    menuFilters,
    setMenuFilters,
    menuLoading,
    selectedTable,

    order,
    posFlags,
    loading,
    busy,
    kitchenPrintStates,

    serviceType,
    setServiceType,
    tablelessOrders,
    tablelessLoading,
    startNewTablelessOrder,
    selectTablelessOrder,
    refreshTablelessOrders: () => loadTablelessOrders(serviceType),

    load,
    refreshCustomers,
    selectTable,
    openOrderById,
    create,
    changeCustomer,
    addItem,
    updateQuantity,
    updateComboQuantity,
    cancelItem,
    confirmOrder,
    reopenOrder,
    cancelOrder,
    transferTable,
    loadTransferTargets,
    loadFreeTables,
    loadOrderItems,
    moveOrderItems,
    mergeOrders,
    splitBill,
    clearSplitBill,
    billCheck,
    ...orderBilling,
  };
};

export default useRestaurantOrders;
